import type { ErrorCode, Problem } from "@zeeta/shared";

export class ApiError extends Error {
  override name = "ApiError";
  constructor(
    readonly status: number,
    readonly code: ErrorCode | "network_error",
    readonly problem: Problem | null,
  ) {
    super(problem?.detail ?? problem?.title ?? code);
  }

  fieldError(path: string): string | undefined {
    return this.problem?.errors?.find((e) => e.path === path)?.message;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: "same-origin",
      headers:
        body === undefined
          ? { Accept: "application/json" }
          : { "Content-Type": "application/json", Accept: "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network_error", null);
  }
  if (res.status === 204) return undefined as T;
  const isJson = (res.headers.get("Content-Type") ?? "").includes("json");
  const data: unknown = isJson ? await res.json() : null;
  if (!res.ok) {
    const problem = (data as Problem | null) ?? null;
    throw new ApiError(res.status, problem?.code ?? "internal_error", problem);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};
